import { CrescLoader } from "../components/LoadingScreen";
import { useEffect, useState, useRef, useCallback } from "react";
import Sidebar from "../components/Sidebar";
import CalendarView from "../components/CalendarView";
import DocumentsView from "../components/DocumentsView";
import Resources from "./Resources";
import Overview from "./Overview";
import WorkspaceDashboard from "./WorkspaceDashboard";
import ProjectDashboard from "./ProjectDashboard";
import Automations from "./Automations";
import WelcomeTour from "../components/WelcomeTour";
import { THEMES } from "../contexts/ThemeContext";
import { useTheme } from "../contexts/ThemeContext";
import { X, Bell, Search, ChevronRight } from "lucide-react";

// ── Content top bar — breadcrumb + actions ────────────────────────────────────
const TAB_LABELS = {
  overview: "Dashboard", calendar: "Calendar", resources: "Resources",
  automations: "Automations", inbox: "Inbox", activity: "Activity",
};

function ContentTopBar({ mainTab, contentTab }) {
  const label = TAB_LABELS[mainTab] || TAB_LABELS[contentTab] || "Overview";
  return (
    <div className="flex-shrink-0 flex items-center justify-between px-7 py-4"
      style={{ borderBottom: "1px solid var(--cresc-border-sub)", background: "var(--cresc-bg)" }}>
      {/* Left: title + breadcrumb */}
      <div>
        <h1 className="text-[18px] font-bold leading-tight tracking-tight" style={{ color: "var(--cresc-text)" }}>
          {label}
        </h1>
        <div className="flex items-center gap-1 mt-0.5">
          <span className="text-[11px]" style={{ color: "var(--cresc-text-4)" }}>Crescendo</span>
          <ChevronRight className="w-3 h-3" style={{ color: "var(--cresc-text-4)" }} />
          <span className="text-[11px]" style={{ color: "var(--cresc-text-3)" }}>{label}</span>
        </div>
      </div>
      {/* Right: search + bell + share */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-[12px]"
          style={{ background: "var(--cresc-surface-2)", border: "1px solid var(--cresc-border-sub)", color: "var(--cresc-text-3)" }}>
          <Search className="w-3.5 h-3.5" />
          <span>Search…</span>
          <span className="text-[10px] px-1 py-0.5 rounded" style={{ background: "var(--cresc-surface-3)" }}>⌘K</span>
        </div>
        <button className="relative w-8 h-8 rounded-xl flex items-center justify-center transition-colors"
          style={{ background: "var(--cresc-surface-2)", border: "1px solid var(--cresc-border-sub)", color: "var(--cresc-text-3)" }}>
          <Bell className="w-3.5 h-3.5" />
        </button>
        <button className="px-3.5 py-1.5 rounded-xl text-[12px] font-semibold transition-colors"
          style={{ background: "var(--cresc-surface-2)", border: "1px solid var(--cresc-border-sub)", color: "var(--cresc-text)" }}>
          Share
        </button>
      </div>
    </div>
  );
}

function GlobalThemePicker({ onClose }) {
  const { themeId, setTheme } = useTheme();
  return (
    <div className="fixed inset-0 z-[600] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="border rounded-2xl p-6 w-full max-w-sm shadow-2xl" style={{ background: "var(--cresc-surface)", borderColor: "var(--cresc-border)" }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-[15px] font-semibold" style={{ color: "var(--cresc-text)" }}>Theme</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg" style={{ color: "var(--cresc-text-3)" }}><X className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {Object.values(THEMES).map(t => (
            <button key={t.id} onClick={() => { setTheme(t.id); onClose(); }}
              className="flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all"
              style={{ borderColor: themeId === t.id ? "var(--cresc-accent)" : "var(--cresc-border)" }}>
              <div className="w-10 h-10 rounded-xl border-2 shadow-lg flex items-end p-1.5" style={{ background: t.preview, borderColor: t.vars["--cresc-accent"] + "60" }}>
                <div className="w-full h-2 rounded" style={{ background: t.vars["--cresc-accent"] }} />
              </div>
              <span className="text-[10px] font-medium" style={{ color: "var(--cresc-text-2)" }}>{t.name}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
import RightPanel from "../components/RightPanel";
import { useWorkspace } from "../context/WorkspaceContext";
import TabletShell, { useIsTablet } from "../components/TabletShell";

import { PanelGroup, Panel, PanelResizeHandle } from "react-resizable-panels";
import { AnnouncementBar } from "../components/AnnouncementBar";
import TeamAvailability from '../components/TeamAvailability';
import InboxPanel from "../components/InboxPanel";
import ActivityFeed from "../components/ActivityFeed";
import GoalsPanel from "../components/GoalsPanel";
import { useBg } from "../hooks/useBg";
import QuickCapture from "../components/QuickCapture";
import TemplatesPanel from "../components/TemplatesPanel";

export default function Dashboard() {
  const { loading } = useWorkspace();
  const [showTour, setShowTour] = useState(false);
  const [mainTab, setMainTab] = useState(() => {
    try { return localStorage.getItem("cresc:mainTab") || "calendar"; } catch { return "calendar"; }
  });
  // contentTab tracks calendar|overview|resources
  const [contentTab, setContentTab] = useState(() => {
    try { return localStorage.getItem("cresc:contentTab") || "overview"; } catch { return "overview"; }
  });
  const handleSetMainTab = useCallback((tab) => {
    setMainTab(tab);
    if (tab === "calendar" || tab === "overview" || tab === "resources" || tab === "automations") setContentTab(tab);
    try { localStorage.setItem("cresc:mainTab", tab); } catch {}
    try { if (tab === "calendar" || tab === "overview" || tab === "resources" || tab === "automations") localStorage.setItem("cresc:contentTab", tab); } catch {}
  }, []);
  const { hasBg, appShellStyle, panelGroupStyle, panelStyle } = useBg();
  const sidebarPanelRef = useRef(null);
  const rightPanelRef = useRef(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showGlobalTheme, setShowGlobalTheme] = useState(false);
  // Tree selection: what's shown in main panel
  const [treeSelection, setTreeSelection] = useState({ type: "overview" }); // type: overview | project | element

  // Tree selection events from Sidebar's ProjectTree
  useEffect(() => {
    const onProject = (e) => setTreeSelection({ type: "project", project: e.detail });
    const onOverview = () => setTreeSelection({ type: "overview" });
    window.addEventListener("crescendo:select-project", onProject);
    window.addEventListener("crescendo:select-overview", onOverview);
    return () => {
      window.removeEventListener("crescendo:select-project", onProject);
      window.removeEventListener("crescendo:select-overview", onOverview);
    };
  }, []);
  useEffect(() => {
    const handler = () => setShowGlobalTheme(true);
    window.addEventListener("crescendo:open-theme", handler);
    return () => window.removeEventListener("crescendo:open-theme", handler);
  }, []);
  const [rightCollapsed, setRightCollapsed] = useState(false);

  // ── Mobile detection ──────────────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [mobileDrawer, setMobileDrawer] = useState(null); // 'sidebar' | 'right' | null
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  const [autoCollapseL, setAutoCollapseL] = useState(() => localStorage.getItem("crescendo:autoCollapseL") === "1");
  const expandTimerL = useRef(null);
  const expandTimerR = useRef(null);
  const [autoCollapseR, setAutoCollapseR] = useState(() => localStorage.getItem("crescendo:autoCollapseR") === "1");

  useEffect(() => {
    try {
      if (localStorage.getItem("crescendo:first-run") === "1") {
        setShowTour(true);
      }
    } catch {}
  }, []);

  useEffect(() => {
    const onOpen = () => setMainTab("resources");
    window.addEventListener("crescendo:open-documents", onOpen);
    return () => window.removeEventListener("crescendo:open-documents", onOpen);
  }, []);

  const toggleSidebar = () => {
    if (sidebarCollapsed) {
      sidebarPanelRef.current?.expand();
    } else {
      sidebarPanelRef.current?.collapse();
    }
  };

  const toggleRight = () => {
    if (rightCollapsed) {
      rightPanelRef.current?.expand();
    } else {
      rightPanelRef.current?.collapse();
    }
  };


  // Global Ctrl+scroll zoom from anywhere in the app
  useEffect(() => {
    const handler = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      window.dispatchEvent(new CustomEvent("crescendo:col-zoom", {
        detail: { dir: e.deltaY > 0 ? -1 : 1 }
      }));
    };
    window.addEventListener("wheel", handler, { passive: false });
    return () => window.removeEventListener("wheel", handler);
  }, []);

  const isTablet = useIsTablet();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--cresc-bg,#0E0E10)]">
        <CrescLoader size={24} color="#A1A1AA" />
      </div>
    );
  }

  // Auto-collapse: only handles EXPANSION when panel is collapsed.
  // Collapse is handled by onMouseLeave on the panel content itself.
  const handleMouseMove = (e) => {
    // Left panel: start 250ms timer to expand when cursor hits the edge
    if (autoCollapseL && sidebarCollapsed && e.clientX <= 20) {
      if (!expandTimerL.current) {
        expandTimerL.current = setTimeout(() => {
          expandTimerL.current = null;
          if (sidebarCollapsed) toggleSidebar();
        }, 250);
      }
    } else if (expandTimerL.current) {
      clearTimeout(expandTimerL.current);
      expandTimerL.current = null;
    }
    // Right panel: same
    if (autoCollapseR && rightCollapsed && e.clientX >= window.innerWidth - 20) {
      if (!expandTimerR.current) {
        expandTimerR.current = setTimeout(() => {
          expandTimerR.current = null;
          if (rightCollapsed) toggleRight();
        }, 250);
      }
    } else if (expandTimerR.current) {
      clearTimeout(expandTimerR.current);
      expandTimerR.current = null;
    }
  };

  // ── Mobile tabs config ────────────────────────────────────────────────────
  const mobileTabs = [
    { id: "overview", label: "Overview", icon: "◈" },
    { id: "calendar", label: "Calendar", icon: "📅" },
    { id: "resources", label: "Resources", icon: "📦" },
    { id: "inbox", label: "Inbox", icon: "📥" },
  ];

  if (isMobile) {
    return (
      <div className="h-screen w-screen flex flex-col bg-[var(--cresc-bg,#0E0E10)]" data-app-shell style={appShellStyle}>
        <AnnouncementBar />
        {/* Sidebar drawer */}
        {mobileDrawer === "sidebar" && (
          <>
            <div className="fixed inset-0 z-[100] bg-black/60" onClick={() => setMobileDrawer(null)} />
            <div className="fixed inset-y-0 left-0 z-[101] w-72 bg-[var(--cresc-bg,#0E0E10)] border-r border-[var(--cresc-border,#2A2A2E)] flex flex-col" style={panelStyle}>
              <Sidebar mainTab={mainTab} setMainTab={(t) => { handleSetMainTab(t); setMobileDrawer(null); }}
                collapsed={false} onToggleCollapse={() => setMobileDrawer(null)} />
            </div>
          </>
        )}
        {/* Right panel drawer */}
        {mobileDrawer === "right" && (
          <>
            <div className="fixed inset-0 z-[100] bg-black/60" onClick={() => setMobileDrawer(null)} />
            <div className="fixed inset-y-0 right-0 z-[101] w-72 bg-[var(--cresc-bg,#0E0E10)] border-l border-[var(--cresc-border,#2A2A2E)] flex flex-col" style={panelStyle}>
              <RightPanel collapsed={false} onToggleCollapse={() => setMobileDrawer(null)} />
            </div>
          </>
        )}
        {/* Main content */}
        <div className="flex-1 min-h-0 overflow-hidden" style={panelStyle}>
          {mainTab === "inbox" ? (
            <div className="flex flex-col h-full bg-[var(--cresc-bg,#0E0E10)]"><InboxPanel fullscreen /></div>
          ) : mainTab === "activity" ? (
            <div className="flex flex-col h-full bg-[var(--cresc-bg,#0E0E10)] overflow-y-auto"><ActivityFeed /></div>
          ) : (
            <>
              <div style={{ display: contentTab === "overview" ? "flex" : "none", flexDirection: "column", height: "100%", minHeight: 0 }}>
                {treeSelection.type === "project" && treeSelection.project
                  ? <ProjectDashboard
                      project={treeSelection.project}
                      onNavigateElement={el => setTreeSelection({ type: "element", element: el })}
                      onSetMainTab={tab => { handleSetMainTab(tab); setTreeSelection({ type: "overview" }); }}
                    />
                  : <WorkspaceDashboard />
                }
              </div>
              <div style={{ display: contentTab === "calendar" ? "flex" : "none", flexDirection: "column", height: "100%", minHeight: 0 }}>
                <CalendarView />
              </div>
              <div style={{ display: contentTab === "resources" ? "flex" : "none", flexDirection: "column", height: "100%", minHeight: 0 }}>
                <Resources />
              </div>
              <div style={{ display: contentTab === "automations" ? "flex" : "none", flexDirection: "column", height: "100%", minHeight: 0 }}>
                <Automations />
              </div>
            </>
          )}
        </div>
        {/* Bottom tab bar */}
        <div className="flex-shrink-0 flex items-center border-t border-[var(--cresc-border,#2A2A2E)] bg-[var(--cresc-bg,#0E0E10)]" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
          <button onClick={() => setMobileDrawer(d => d === "sidebar" ? null : "sidebar")}
            className={`flex flex-col items-center justify-center gap-0.5 flex-1 py-2.5 transition-colors ${mobileDrawer === "sidebar" ? "text-[#5A90F5]" : "text-[#52525B]"}`}>
            <span className="text-base leading-none">☰</span>
            <span className="text-[9px]">Menu</span>
          </button>
          {mobileTabs.map(tab => (
            <button key={tab.id} onClick={() => { handleSetMainTab(tab.id); setMobileDrawer(null); }}
              className={`flex flex-col items-center justify-center gap-0.5 flex-1 py-2.5 transition-colors ${mainTab === tab.id ? "text-[#5A90F5]" : "text-[#52525B]"}`}>
              <span className="text-base leading-none">{tab.icon}</span>
              <span className="text-[9px]">{tab.label}</span>
            </button>
          ))}
          <button onClick={() => setMobileDrawer(d => d === "right" ? null : "right")}
            className={`flex flex-col items-center justify-center gap-0.5 flex-1 py-2.5 transition-colors ${mobileDrawer === "right" ? "text-[#5A90F5]" : "text-[#52525B]"}`}>
            <span className="text-base leading-none">⋮</span>
            <span className="text-[9px]">More</span>
          </button>
        </div>
        {showTour && <WelcomeTour onClose={() => setShowTour(false)} />}
        <QuickCapture />
      </div>
    );
  }

  return (
    <div className="h-screen w-screen flex flex-col bg-[var(--cresc-bg,#0E0E10)]"
      onMouseMove={autoCollapseL || autoCollapseR ? handleMouseMove : undefined} data-app-shell style={appShellStyle}>
      <AnnouncementBar />
      <div className="flex-1 min-h-0 flex" style={panelGroupStyle}>
        <PanelGroup direction="horizontal" autoSaveId="crescendo-layout-v2" style={panelGroupStyle}>
          {/* LEFT — main nav + workspace browser. */}
          <Panel
            ref={sidebarPanelRef}
            defaultSize={20} minSize={15} maxSize={32}
            collapsible collapsedSize={0}
            id="sidebar" order={1}
            onCollapse={() => setSidebarCollapsed(true)}
            onExpand={() => setSidebarCollapsed(false)}
          >
            <div className="h-full" style={panelStyle}>
              <Sidebar mainTab={mainTab} setMainTab={handleSetMainTab}
              autoCollapse={autoCollapseL}
              onAutoCollapseLeave={autoCollapseL ? (() => { if (!sidebarCollapsed) toggleSidebar(); }) : undefined}
              onToggleAutoCollapse={() => { const v = !autoCollapseL; setAutoCollapseL(v); localStorage.setItem("crescendo:autoCollapseL", v?"1":"0"); }}
                collapsed={sidebarCollapsed} onToggleCollapse={toggleSidebar} />
            </div>
          </Panel>
          <PanelResizeHandle className="group relative w-1.5 cursor-col-resize flex-shrink-0">
            <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-[#2A2A2A] group-hover:bg-[#5A90F5] transition-colors" />
          </PanelResizeHandle>
          {/* CENTER — active view (calendar/documents). */}
          <Panel defaultSize={62} minSize={40} id="main" order={2}>
            <div className="h-full flex flex-col" data-cresc-mainpanel style={panelStyle}>
              {/* ── Content top bar — reference-inspired ── */}
              <ContentTopBar mainTab={mainTab} contentTab={contentTab} />
              <div className="flex-1 min-h-0 flex flex-col" style={panelStyle}>
{mainTab === "inbox" ? (
                  <div className="flex flex-col h-full bg-[var(--cresc-bg,#0F1020)]"><InboxPanel fullscreen /></div>
                ) : mainTab === "activity" ? (
                  <div className="flex flex-col h-full bg-[var(--cresc-bg,#0F1020)] overflow-y-auto"><ActivityFeed /></div>
                ) : (
                  // All environments stay mounted — only visibility toggles.
                  <>
                    <div style={{ display: contentTab === "overview" ? "flex" : "none", flexDirection: "column", height: "100%", minHeight: 0 }}>
                      {treeSelection.type === "project" && treeSelection.project
                        ? <ProjectDashboard
                            project={treeSelection.project}
                            onNavigateElement={el => setTreeSelection({ type: "element", element: el })}
                            onSetMainTab={tab => { handleSetMainTab(tab); setTreeSelection({ type: "overview" }); }}
                          />
                        : <WorkspaceDashboard />
                      }
                    </div>
                    <div style={{ display: contentTab === "calendar" ? "flex" : "none", flexDirection: "column", height: "100%", minHeight: 0 }}>
                      <CalendarView />
                    </div>
                    <div style={{ display: contentTab === "resources" ? "flex" : "none", flexDirection: "column", height: "100%", minHeight: 0 }}>
                      <Resources />
                    </div>
                    <div style={{ display: contentTab === "automations" ? "flex" : "none", flexDirection: "column", height: "100%", minHeight: 0 }}>
                      <Automations />
                    </div>
                  </>
                )}
              </div>
            </div>
          </Panel>
          <PanelResizeHandle className="group relative w-1.5 cursor-col-resize flex-shrink-0">
            <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-[#2A2A2A] group-hover:bg-[#5A90F5] transition-colors" />
          </PanelResizeHandle>
          <Panel
            ref={rightPanelRef}
            defaultSize={18} minSize={14} maxSize={28}
            collapsible collapsedSize={0}
            id="right-panel" order={3}
            onCollapse={() => setRightCollapsed(true)}
            onExpand={() => setRightCollapsed(false)}
          >
            <RightPanel
              collapsed={rightCollapsed} onToggleCollapse={toggleRight}
              autoCollapse={autoCollapseR}
              onAutoCollapseLeave={autoCollapseR ? (() => { if (!rightCollapsed) toggleRight(); }) : undefined}
              onToggleAutoCollapse={() => { const v = !autoCollapseR; setAutoCollapseR(v); localStorage.setItem("crescendo:autoCollapseR", v?"1":"0"); }} />
          </Panel>
        </PanelGroup>
      </div>
      {showTour && <WelcomeTour onClose={() => setShowTour(false)} />}
      <QuickCapture />
      {showGlobalTheme && <GlobalThemePicker onClose={() => setShowGlobalTheme(false)} />}
      {/* Floating expand buttons when panels are collapsed */}
      {sidebarCollapsed && (
        <button
          onClick={toggleSidebar}
          className="fixed left-0 top-4 z-50 w-5 h-10 flex items-center justify-center bg-[#1a1a1a] border border-[var(--cresc-border,#2A2A2E)] border-l-0 rounded-r text-[#A1A1AA] hover:text-[#EDEDED] hover:bg-[#2A2A2A] transition-colors font-mono text-[11px] font-bold"
          title="Expand sidebar"
        >{">>"}</button>
      )}
      {rightCollapsed && (
        <button
          onClick={toggleRight}
          className="fixed right-0 top-4 z-50 w-5 h-10 flex items-center justify-center bg-[#1a1a1a] border border-[var(--cresc-border,#2A2A2E)] border-r-0 rounded-l text-[#A1A1AA] hover:text-[#EDEDED] hover:bg-[#2A2A2A] transition-colors font-mono text-[11px] font-bold"
          title="Expand right panel"
        >{"<<"}</button>
      )}
    </div>
  );
}
